import { AnalyticsEventTopic } from '~/modules/analytics/analyticsHelper';
import { AI_OVERVIEW_EVENT_NAME, DataCloudEventTypeValues } from '~/modules/analytics/datacloudEventTypes/constants';
import { DataCloudEventType, DataCloudIdentityData } from '~/modules/analytics/datacloudEventTypes/datacloudEventType';
import { DataCloudEventData } from '~/modules/services/analyticsService';
import { randomUUID } from '~/modules/utilities/cross_env_utils';

import { AiOverviewFeedbackStatus } from './aiOverviewFeedbackDataCloudEventType';

export interface AiOverviewCardClickDataCloudAnalyticsEvent {
    interaction: {
        name: typeof AI_OVERVIEW_EVENT_NAME;
        eventType: typeof DataCloudEventTypeValues.aiOverviewCardClick;
        deviceId: string;
        searchQueryUid: string;
        'co-relation-id': string;
        aiOverviewStatus: AiOverviewFeedbackStatus;
        aiOverviewListingCount: number;
        aiOverviewPosition: number;
        listingId: string;
        anonymous?: '0' | '1';
        language?: string;
        userAgent?: string;
        source?: string;
        searchString?: string;
        TokenCount?: number;
        TTFT?: number;
    };
    user: {
        attributes: DataCloudIdentityData & {
            eventType: string;
            deviceId: string;
        };
    };
}

export interface AiOverviewCardClickEventData extends DataCloudEventData {
    aiOverviewListingCount?: number;
    aiOverviewPosition?: number;
    aiOverviewStatus?: AiOverviewFeedbackStatus;
    aiOverviewTokenCount?: number;
    aiOverviewTtft?: number;
    aiOverviewCorrelationId?: string;
    listingId?: string;
    searchQueryUid?: string;
    searchString?: string;
    source?: string;
}

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

const isFiniteNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

export class AiOverviewCardClickDataCloudEventType extends DataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_AI_OVERVIEW_CARD_CLICK;
    }

    getBody(event: AiOverviewCardClickEventData): AiOverviewCardClickDataCloudAnalyticsEvent {
        const baseEvent = this.baseBody(event);
        const status: AiOverviewFeedbackStatus = event.aiOverviewStatus ?? 'idle';
        const listingCount = isFiniteNumber(event.aiOverviewListingCount)
            ? Math.max(0, event.aiOverviewListingCount)
            : 0;
        const position = isFiniteNumber(event.aiOverviewPosition) ? clamp(event.aiOverviewPosition, 0, 49) : 0;

        const interaction: AiOverviewCardClickDataCloudAnalyticsEvent['interaction'] = {
            name: AI_OVERVIEW_EVENT_NAME,
            eventType: DataCloudEventTypeValues.aiOverviewCardClick,
            deviceId: baseEvent.username,
            searchQueryUid: event.searchQueryUid ?? '',
            'co-relation-id': event.aiOverviewCorrelationId || randomUUID(),
            aiOverviewStatus: status,
            aiOverviewListingCount: listingCount,
            aiOverviewPosition: position,
            listingId: event.listingId ?? '',
            anonymous: baseEvent.anonymous ? '1' : '0',
            language: baseEvent.language,
            userAgent: baseEvent.userAgent,
        };

        const source = event.source?.trim();
        if (source) {
            interaction.source = source;
        }

        const searchString = event.searchString?.trim();
        if (searchString) {
            interaction.searchString = searchString;
        }

        if (isFiniteNumber(event.aiOverviewTokenCount)) {
            interaction.TokenCount = clamp(event.aiOverviewTokenCount, 0, 100_000);
        }

        if (isFiniteNumber(event.aiOverviewTtft)) {
            interaction.TTFT = clamp(event.aiOverviewTtft, 0, 60_000);
        }

        return {
            interaction,
            user: {
                attributes: {
                    ...baseEvent.userData,
                    eventType: DataCloudEventTypeValues.identity,
                    deviceId: baseEvent.username,
                },
            },
        };
    }
}
